// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a page preview inside the message's bubble, at the bubble's width. A page with a picture
// shows it full width over the site, title and two lines of description; a YouTube video plays in
// that spot. A page with only a logo, or nothing but a title (a sign-in page), is one compact row.
// The whole card opens the page; the site's icon and domain always say where it goes.

import styles from '@app/features/channel/components/embeds/channel_embed/BubbleLinkPreview.module.css';
import {
	type EmbedProps,
	getOptimizedMediaURL,
	isMediaMatureContent,
	isValidMedia,
} from '@app/features/channel/components/embeds/channel_embed/ChannelEmbedShared';
import {EmbedLink} from '@app/features/channel/components/embeds/channel_embed/EmbedLink';
import {
	cleanLinkTitle,
	hasLargeThumbnail,
	linkPreviewDomain,
	youTubePlayerEmbed,
} from '@app/features/channel/components/embeds/channel_embed/LinkPreview';
import {useSiteIcon} from '@app/features/channel/components/embeds/channel_embed/SiteIcons';
import {EmbedYouTube} from '@app/features/channel/components/embeds/media/EmbedYouTube';
import messageStyles from '@app/features/theme/styles/Message.module.css';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {GlobeIcon} from '@app/features/ui/icons/Icons';
import type {EmbedMedia} from '@fluxer/schema/src/domains/message/EmbedSchemas';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type {FC} from 'react';
import {useMemo, useState} from 'react';

/** The picture is fetched at twice the widest bubble, for sharp screens. */
const PICTURE_WIDTH = 960;
const THUMB_SIZE = 112;
/** Wide pictures keep their shape; tall ones are cropped to this, so a card never towers. */
const MIN_PICTURE_RATIO = 1.5;
const MAX_PICTURE_RATIO = 2.4;

function pictureOf(media: EmbedMedia | undefined): Required<EmbedMedia> | null {
	return isValidMedia(media) && !isMediaMatureContent(media) ? media : null;
}

export const BubbleLinkPreview: FC<Pick<EmbedProps, 'embed'>> = observer(({embed}) => {
	const youTube = useMemo(() => youTubePlayerEmbed(embed), [embed]);
	const url = embed.url ?? '';
	const domain = linkPreviewDomain(url) ?? embed.provider?.name ?? url;
	const title = cleanLinkTitle(youTube?.title ?? embed.title)?.trim() || domain;
	const description = youTube ? null : embed.description?.trim() || null;
	const image = pictureOf(embed.image);
	const thumbnail = pictureOf(embed.thumbnail);
	const picture = image ?? (thumbnail && hasLargeThumbnail(embed) ? thumbnail : null);
	const logo = picture ? null : thumbnail;
	const siteIcon = useSiteIcon(url);
	const [iconBroken, setIconBroken] = useState(false);
	const icon = iconBroken ? null : siteIcon;
	// A row without the page's own logo shows the site's icon large in its tile instead.
	const iconInTile = !picture && !youTube && !logo && icon !== null;
	const iconImage = (className: string) =>
		icon && (
			<img
				className={className}
				src={icon}
				alt=""
				loading="lazy"
				draggable={false}
				onError={() => setIconBroken(true)}
				data-flx="channel.embeds.bubble-link-preview.site-icon"
			/>
		);
	const site = (
		<span className={styles.site} data-flx="channel.embeds.bubble-link-preview.site">
			{!iconInTile && iconImage(styles.siteIcon)}
			<span className={styles.domain} data-flx="channel.embeds.bubble-link-preview.domain">
				{domain}
			</span>
		</span>
	);
	const words = (
		<span className={styles.words} data-flx="channel.embeds.bubble-link-preview.words">
			<span className={styles.title} data-flx="channel.embeds.bubble-link-preview.title">
				{title}
			</span>
			{description && (
				<span className={styles.description} data-flx="channel.embeds.bubble-link-preview.description">
					{description}
				</span>
			)}
		</span>
	);
	if (youTube || picture) {
		const ratio = picture
			? Math.min(MAX_PICTURE_RATIO, Math.max(MIN_PICTURE_RATIO, picture.width / picture.height))
			: 16 / 9;
		return (
			<div className={styles.card} data-flx="channel.embeds.bubble-link-preview.card">
				{youTube ? (
					<FocusRing
						within
						ringClassName={messageStyles.mediaFocusRing}
						data-flx="channel.embeds.bubble-link-preview.player-ring"
					>
						<div className={styles.player} data-flx="channel.embeds.bubble-link-preview.player">
							<EmbedYouTube
								embed={youTube}
								width={PICTURE_WIDTH / 2}
								data-flx="channel.embeds.bubble-link-preview.embed-you-tube"
							/>
						</div>
					</FocusRing>
				) : null}
				<EmbedLink url={url} className={styles.link} data-flx="channel.embeds.bubble-link-preview.link">
					{picture && !youTube && (
						<img
							className={styles.picture}
							style={{aspectRatio: ratio}}
							src={getOptimizedMediaURL(
								picture.proxy_url,
								PICTURE_WIDTH,
								Math.round(PICTURE_WIDTH / ratio),
								picture.content_type,
							)}
							alt={picture.description ?? ''}
							loading="lazy"
							draggable={false}
							data-flx="channel.embeds.bubble-link-preview.picture"
						/>
					)}
					<span className={styles.body} data-flx="channel.embeds.bubble-link-preview.body">
						{site}
						{words}
					</span>
				</EmbedLink>
			</div>
		);
	}
	return (
		<div className={clsx(styles.card, styles.row)} data-flx="channel.embeds.bubble-link-preview.card--row">
			<EmbedLink
				url={url}
				className={clsx(styles.link, styles.rowLink)}
				data-flx="channel.embeds.bubble-link-preview.row-link"
			>
				{logo ? (
					<img
						className={styles.thumb}
						src={getOptimizedMediaURL(logo.proxy_url, THUMB_SIZE, THUMB_SIZE, logo.content_type)}
						alt=""
						loading="lazy"
						draggable={false}
						data-flx="channel.embeds.bubble-link-preview.thumb"
					/>
				) : (
					<span
						className={clsx(styles.thumb, styles.thumbIcon, iconInTile && styles.thumbSite)}
						data-flx="channel.embeds.bubble-link-preview.thumb-icon"
					>
						{iconInTile ? (
							iconImage(styles.tileIcon)
						) : (
							<GlobeIcon size={22} data-flx="channel.embeds.bubble-link-preview.globe-icon" />
						)}
					</span>
				)}
				<span className={styles.body} data-flx="channel.embeds.bubble-link-preview.body--row">
					{words}
					{site}
				</span>
			</EmbedLink>
		</div>
	);
});
